import { Button } from '@ibsys/kit';
import { IconButton } from '@/shared/ui';
import { XIcon } from 'lucide-react';
import { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { ConversationSelectionSource } from '@/entities/conversations';
import { useTablePresets } from '@/entities/settings';
import { formatHumanDate } from '@/shared/lib';
import { PresetOfferBar } from './PresetOfferBar';

type Props = {
  /**
   * Ключ рабочей записи в `settings.tables`: имя таблицы (например, `'dialogsListV2'`) либо
   * ключ раздела — пресет сохраняется из вида того раздела, в котором открыта выборка.
   */
  sectionKey: string;
  /** Несохранённая выборка (описание + источник); `null` — предложение не показывается */
  selection: { description: string; source: ConversationSelectionSource } | null;
  /** Пользователь скрыл предложение, не сохраняя выборку */
  onDismiss: () => void;
};

/** i18n-ключи автоимени пресета по источнику выборки. */
const PRESET_NAME_KEY = {
  autoInsight: 'tablePresets:autoInsightPresetName',
  qualityZone: 'tablePresets:qualityZonePresetName',
  consultantRating: 'tablePresets:consultantRatingPresetName',
  criteriaAverage: 'tablePresets:criteriaAveragePresetName',
  lowScore: 'tablePresets:lowScorePresetName',
  distribution: 'tablePresets:distributionPresetName',
  trend: 'tablePresets:trendPresetName',
  alert: 'tablePresets:alertPresetName',
  customChart: 'tablePresets:customChartPresetName',
} as const satisfies Record<ConversationSelectionSource, string>;

/**
 * Предложение сохранить внешнюю выборку в пресет таблицы.
 *
 * Показывается, пока список открыт по выборке, посчитанной снаружи, она не сохранена
 * и предложение не скрыто пользователем: это единственная подсказка, что список сужен, а не полон.
 * Название пресета собирается автоматически из описания выборки — спрашивать его у пользователя
 * незачем.
 * Шаблон имени зависит от источника выборки: «Авто-инсайт N. …», «Зона качества N. …»,
 * «Критерий N. …» и так далее по показателю дашборда, с которого пришла выборка.
 *
 * Ни сохранение, ни скрытие предложения выборку не снимают: список остаётся суженным.
 */
export const SelectionSaveOffer: FC<Props> = ({ sectionKey, selection, onDismiss }) => {
  const { t, i18n } = useTranslation(['tablePresets']);
  const { presets, savePresetAs } = useTablePresets(sectionKey);

  const handleSave = useCallback(() => {
    if (!selection) return;
    // Номер разводит одноимённые выборки одного источника: он на единицу больше, чем уже
    // сохранено пресетов того же источника у этого пользователя. Считаем по источнику, а не
    // по префиксу имени: имя локализовано, и при смене языка нумерация начиналась бы заново.
    const number = presets.filter((preset) => preset.selectionSource === selection.source).length + 1;
    // Дата попадает в имя всех показателей, кроме авто-инсайта: его шаблон параметр `date`
    // игнорирует, поэтому передаём дату безусловно.
    const date = formatHumanDate(i18n, new Date());
    const name = t(PRESET_NAME_KEY[selection.source], {
      number,
      description: selection.description,
      date,
    });
    savePresetAs(name, selection.source);
  }, [selection, presets, savePresetAs, t, i18n]);

  if (!selection) return null;

  return (
    <PresetOfferBar hint={t('tablePresets:selectionSaveHint')}>
      <Button size="sm" onClick={handleSave}>
        {t('tablePresets:selectionSave')}
      </Button>
      <IconButton
        variant="ghost"
        size="icon"
        className="shrink-0"
        label={t('tablePresets:selectionDismiss')}
        icon={<XIcon className="size-4" />}
        onClick={onDismiss}
      />
    </PresetOfferBar>
  );
};
